// SPDX-License-Identifier: AGPL-3.0-or-later
const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined, dp = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return dp > 0
    ? n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : NF.format(Math.round(n));
}

/** Compact magnitude — 7.7M, 340k, 1,234. */
export function compact(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(dp)}bn`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(dp)}M`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(0)}k`;
  return NF.format(Math.round(n));
}

/** Money — $21.8bn, $479m, $1.2m, $54,000. */
export function money(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(1)}bn`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(0)}m`;
  return `$${NF.format(Math.round(n))}`;
}

/** Percentage from a FRACTION (0.19 → "19.3%"). */
export function pct(frac: number | null | undefined, dp = 1): string {
  if (frac === null || frac === undefined || !Number.isFinite(frac)) return '—';
  return `${(frac * 100).toFixed(dp)}%`;
}
/** Percentage from an ALREADY-PERCENT value (13 → "13%"). */
export function pctv(v: number | null | undefined, dp = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}
/** Signed change from a fraction. */
export function delta(frac: number | null | undefined, dp = 0): string {
  if (frac === null || frac === undefined || !Number.isFinite(frac)) return '—';
  return `${frac > 0 ? '+' : ''}${(frac * 100).toFixed(dp)}%`;
}

export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

/* ── class value formatting ── */
export type ClassId = 'agricultural' | 'water' | 'residential' | 'commercial' | 'business' | 'mining';

/** Format a class's native value for display. Water is stored in megalitres and
 *  shown in gigalitres (its natural unit). */
export function fmtValue(cid: ClassId, v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  switch (cid) {
    case 'agricultural': return `${compact(v)} ha`;
    case 'water': return `${compact(v / 1000)} GL`;
    case 'commercial':
    case 'business':
    case 'mining':
    case 'residential': return num(v);
  }
}
export const CLASS_UNIT_SHORT: Record<ClassId, string> = {
  agricultural: 'ha', water: 'GL', residential: 'buyers', commercial: 'sites', business: 'interests', mining: 'tenements',
};

/* ── friendlier short country names for tight labels ── */
const SHORT_NAME: Record<string, string> = {
  "People's Republic of China": 'China',
  'United States of America': 'United States',
  'United Kingdom': 'United Kingdom',
  'Saint Vincent and the Grenadines': 'St Vincent',
  'United Arab Emirates': 'UAE',
  'British Virgin Islands': 'British Virgin Is.',
  'Papua New Guinea': 'PNG',
};
export const shortName = (name: string): string => SHORT_NAME[name] ?? name;

/* ── flag emoji from ISO-3166 alpha-2 ── */
export function flag(iso: string | null | undefined): string {
  if (!iso || iso.length !== 2) return '';
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (iso.charCodeAt(0) - 65), A + (iso.charCodeAt(1) - 65));
}

/* ── stable colour per country, carried across every view ── */
const COUNTRY_COLOUR: Record<string, string> = {
  GB: '#3d6fb4', // United Kingdom — blue
  CN: '#c0392b', // China — red
  US: '#2f6f57', // United States — green (site accent family)
  CA: '#d1495b', // Canada — rose
  CH: '#8a6d3b', // Switzerland
  NL: '#e08a1e', // Netherlands — orange
  DE: '#5b6b8c', // Germany
  ZA: '#6b8f3a', // South Africa
  BS: '#c98a3a', // Bahamas
  MC: '#9c6a8a', // Monaco
  VN: '#b8863b', // Vietnam
  SG: '#9b59b6', // Singapore — purple
  JP: '#c94f7c', // Japan — pink
  KY: '#7f8c8d', // Cayman Islands — grey (tax haven)
  HK: '#b8863b',
  KR: '#4f8a9c',
  SA: '#7d9b3a',
  FR: '#4361a8',
  AE: '#9a7b4f',
  BM: '#6d7f9c',
  LU: '#95a5a6',
  VG: '#78909c',
};
const FALLBACK = ['#4c6b8a', '#8a5a7a', '#5a8a6b', '#9a7b4f', '#6b6b9a', '#8a6b4f', '#4f8a8a', '#9a5a5a'];
export function countryColour(iso: string | null | undefined, name = ''): string {
  if (iso && COUNTRY_COLOUR[iso]) return COUNTRY_COLOUR[iso];
  const key = (iso || name) ?? '';
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  return FALLBACK[h % FALLBACK.length];
}

/* ── states ── */
export const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
export const STATE_NAME: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory',
};

/* ── sequential ramp: pale → deep teal (more foreign ownership = deeper) ── */
export const SHARE_RAMP = ['#eaf2ef', '#c3ddd3', '#8fc0ae', '#4f9d84', '#1f6b56'];
export const NO_DATA = '#e2e2db';
/** Bucket a value against ascending thresholds → ramp colour. */
export function rampFor(value: number | null | undefined, thresholds: number[], ramp = SHARE_RAMP): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_DATA;
  let i = 0;
  while (i < thresholds.length && value >= thresholds[i]) i++;
  return ramp[i];
}
